import React from 'react';
import { get, isNumber, orderBy, escapeRegExp } from 'lodash';
import { metricKeys, MetricTypes } from '@pulse/metrics';
import { UNKNOWN_NAME } from '@utils/constants';
import { calculatePctShareMax, getNumValueKeys } from '@utils/table/pctShare';
import { DIMENSION_TYPES_CONSTANTS, getMetricUnit } from '@utils/metrics';
import { MF_LABEL } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import { getMetricMetaByKey } from '@src/utils/metrics/metricHelpers';
import MetricHeader from './MetricHeader';
import ActionHeader from './ActionHeader';
import ActionCell from './ActionCell';
import ValueCell from './ValueCell';
import Cell from './Cell';
import Header from './Header';
import ErrorCell from './ErrorCell';
import { Trend } from '@components/common';
import { getDodDiff } from '@src/components/metrics-panel-hz/utils/calculateDodTotal';

export const DIMENSION_COL_WIDTH = 250;
export const METRIC_COL_WIDTH = 145;
export const ACTION_COL_WIDTH = 65;
export const SF_COL_WIDTH = 80;

const COLUMN_HEADER_METRIC_HELPTEXT = {};

const COLUMN_HEADER_METRIC_FILTER_HELPTEXT = {
  [metricKeys.UniqueDevices]: 'Impacted Unique Devices',
  [metricKeys.UniqueDevicesWithAttempts]: 'Impacted Unique Devices',
};

const COLUMN_HEADER_METRIC_FILTER_PCT_HELPTEXT = {
  [metricKeys.UniqueDevices]: 'Percent of Impacted Unique Devices',
  [metricKeys.UniqueDevicesWithAttempts]: 'Percent of Impacted Unique Devices',
};

export const getMetricFilterMetric = (metric) => `${metric}::metric`;
export const getMetricFilterPctMetric = (metric) => `${metric}::metricPct`;

// for unknown filtering IF server was sending the name as Unknown and value as CONVIVA_INTERNAL_UKNOWN for all unknowns records
// the one from backend and the ones where server lookup failed like Geo/ASN/ISP.
// The unknown records were sorted to the bottom using the value CONVIVA_INTERNAL_UKNOWN
// To support filtering on all unknowns, the original value is sent to client but name will remain as `Unknown` and will be used for sorting
// If a customer has a record with name Unknown it will be pushed to the bottom.

const addMetricFilterTag = (row = {}) => {
  const taggedRow = {};
  for (const [key, value] of Object.entries(row)) {
    taggedRow[getMetricFilterMetric(key)] = value;
  }
  return taggedRow;
};

const calcPct = (metaValue, metricValue) => {
  try {
    const pct = (metricValue * 100) / metaValue;
    return isNaN(pct) || !isFinite(pct) ? 'NA' : pct;
  } catch (err) {
    return 'NA';
  }
};

const addRelativePctMetric = (metrics, row) => {
  metrics.forEach((metric) => {
    const unit = getMetricUnit({ metricKey: metric });
    if (unit === '%') {
      row[getMetricFilterPctMetric(metric)] = 'NA';
    } else {
      const metaValue = row[metric];
      const metricValue = row[getMetricFilterMetric(metric)];
      row[getMetricFilterPctMetric(metric)] = calcPct(metaValue, metricValue);
    }
  });
};

// for actions on cell like quick compare and add rule
const getActionColumn = (dimension) => ({
  title: '',
  dataKey: '-',
  key: 'action',
  dimension,
  width: ACTION_COL_WIDTH,
  minWidth: ACTION_COL_WIDTH,
  frozen: 'left',
  resizable: false,
  cellRenderer: ({ column, rowData }) => <ActionCell column={column} rowData={rowData} />,
  headerRenderer: (props) => <ActionHeader {...props} />,
});

export const formatColumns = ({ dimension, metrics, showNAForMetric, realTimeOnTrends }) => {
  const columnConfig: any = realTimeOnTrends ? [] : [getActionColumn(dimension)];
  // first column is the dimension value
  columnConfig.push({
    title: '',
    dataKey: 'name',
    key: dimension,
    width: DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    frozen: 'left',
    resizable: true,
    cellRenderer: ({ cellData, column, rowData }) => {
      return <Cell cellData={cellData} column={column} rowData={rowData} />;
    },
    headerRenderer: (props) => <Header {...props} />,
  });

  // remaining columns are the metric values
  metrics.forEach((metric, idx) => {
    const metricInfo = getMetricMetaByKey({ metricKey: metric });
    const showNA = showNAForMetric ? showNAForMetric(metric) : false;
    // const evenCol = (idx + 1) % 2 === 0;
    columnConfig.push({
      title: metricInfo.short,
      dataKey: metric,
      key: metric,
      metric,
      metricInfo,
      flexGrow: 1,
      align: 'right',
      width: METRIC_COL_WIDTH,
      minWidth: METRIC_COL_WIDTH,
      sortable: true,
      cellRenderer: ({ cellData, column, container, rowData }) => {
        return (
          <ValueCell
            cellData={showNA ? 'NA' : cellData}
            column={column}
            container={container}
            rowData={rowData}
          />
        );
      },
      headerRenderer: (props) => <MetricHeader {...props} />,
      className: 'table-zebra-even',
    });
  });

  return columnConfig;
};

// format data for table
export function formatData({ data = {}, sortBy, searchText = '', hideUnknownRows = false }) {
  const { rows = [], totals = {} }: any = data;

  // move unknown rows to bottom
  let orderedRows: any = [];
  const nanRows: unknown[] = [];
  const unknownRows: any = [];
  rows.forEach((row) => {
    const name = row.name.join('');
    if (UNKNOWN_NAME.includes(name)) {
      unknownRows.push({ ...row, disableDrilldown: false });
      return;
    }

    // Filter out all NaN, such as "NA"
    // because the presence of NA will cause _.orderBy not to work properly
    if (sortBy?.key && !isNumber(get(row, sortBy.key))) {
      nanRows.push(row);
      return;
    }

    orderedRows.push(row);
  });

  if (sortBy) {
    orderedRows = orderBy(orderedRows, [sortBy.key], [sortBy.order]);
  }

  const pctShareMaxValues = calculatePctShareMax({
    data: orderedRows,
    metrics: getNumValueKeys(totals),
  });

  totals.name = ['Total'];
  totals.disableDrilldown = true;
  totals.hidePctShare = true;
  totals.totalRow = true;

  let filteredRows = orderedRows.concat(nanRows, hideUnknownRows ? [] : unknownRows);

  // search
  if (searchText) {
    const regex = new RegExp(escapeRegExp(searchText), 'i'); // ignore case
    filteredRows = filteredRows.filter((row) => regex.test(row.name.join('')));
  }

  return { rows: filteredRows, totals, pctShareMaxValues };
}

// format data for table
export function formatAppData({
  data = {},
  sortBy,
  searchText = '',
  columnMetrics,
  appFilterMode,
  currentDimensionType,
  hideUnknownRows = false,
  getUnknownName,
}: {
  data: Record<string, any>;
  sortBy: {
    key: string;
    order: 'desc' | 'asc';
  };
  searchText?: string;
  appFilterMode: string;
  columnMetrics: string[];
  hideUnknownRows?: boolean;
  currentDimensionType: string;
  getUnknownName?: (data: Record<string, any>) => any;
}) {
  const { rows = [], totals = {} }: any = data;
  // move unknown rows to bottom
  let orderedRows: any = [];
  const nanRows: unknown[] = [];
  const unknownRows: any = [];
  rows.forEach((row) => {
    const name = getUnknownName ? getUnknownName(row) : row.name.join('');
    if (UNKNOWN_NAME.includes(name)) {
      // disable drill-down for unknown rows
      unknownRows.push({ ...row, disableDrilldown: false });
      return;
    }

    // Filter out all NaN, such as "NA"
    // because the presence of NA will cause _.orderBy not to work properly
    if (sortBy?.key && !isNumber(get(row, sortBy.key))) {
      nanRows.push(row);
      return;
    }

    orderedRows.push(row);
  });

  if (sortBy) {
    orderedRows = orderBy(orderedRows, [sortBy.key], [sortBy.order]);
  }

  totals.name = ['Total'];
  totals.disableDrilldown = true;
  totals.hidePctShare = true;
  totals.totalRow = true;

  let filteredRows = orderedRows.concat(nanRows, hideUnknownRows ? [] : unknownRows);

  // search
  if (searchText) {
    const regex = new RegExp(escapeRegExp(searchText), 'i'); // ignore case
    filteredRows = filteredRows.filter((row) => regex.test(row.name.join('')));
  }
  const isAppModeTypeMappedEvent = appFilterMode === DIMENSION_TYPES_CONSTANTS.mappedEventName;
  const isAppModeTypeEventTagKeyValue =
    appFilterMode === DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata;
  const isCurrentDimensionTypeTagKeyValue =
    currentDimensionType === DIMENSION_TYPES_CONSTANTS.eventTagKeyValueMetadata;
  const isCurrentDimensionTypeMappedEventTagKeyValue =
    isCurrentDimensionTypeTagKeyValue ||
    currentDimensionType === DIMENSION_TYPES_CONSTANTS.mappedEventName;

  columnMetrics.forEach((metric) => {
    const metricInfo = getMetricMetaByKey({ metricKey: metric });
    if (!metricInfo) {
      return;
    }
    const isCurrentMetricIsGeneralMetric = metricInfo.type === MetricTypes.appGeneralMetrics;
    const isCurrentMetricIsEvenOnlyMetric = metricInfo.type === MetricTypes.appEventMetrics;
    const isCurrentMetricIsEventTagKeyValueMetric =
      isCurrentMetricIsGeneralMetric || isCurrentMetricIsEvenOnlyMetric;
    const isNAOnMappedEventSpecialMetrics =
      isAppModeTypeMappedEvent &&
      !isCurrentMetricIsEventTagKeyValueMetric &&
      isCurrentDimensionTypeMappedEventTagKeyValue;
    const isNAOnEventTagKeyValueEventSpecialMetrics =
      isAppModeTypeEventTagKeyValue && !isCurrentMetricIsEventTagKeyValueMetric;
    const isNAOnNotMappedEventSpecialMetrics =
      !isAppModeTypeMappedEvent &&
      !isCurrentMetricIsEventTagKeyValueMetric &&
      isCurrentDimensionTypeMappedEventTagKeyValue;
    const isNAOnNotEventTagValueSpecialMetrics =
      !isAppModeTypeEventTagKeyValue &&
      !isCurrentMetricIsEventTagKeyValueMetric &&
      isCurrentDimensionTypeMappedEventTagKeyValue;
    if (
      isNAOnEventTagKeyValueEventSpecialMetrics ||
      isNAOnMappedEventSpecialMetrics ||
      isNAOnNotMappedEventSpecialMetrics ||
      isNAOnNotEventTagValueSpecialMetrics
    ) {
      filteredRows.map((row: any) => {
        row[metric] = 'NA';
      });
      totals[metric] = 'NA';
    }
  });

  const pctShareMaxValues = calculatePctShareMax({
    data: filteredRows,
    metrics: getNumValueKeys(totals),
  });

  return { rows: filteredRows, totals, pctShareMaxValues };
}

export const formatMultiColumns = ({
  dimension,
  metrics,
  mfPctChecked = false,
  showNAForMetric,
  realTimeOnTrends,
  showTrend = false,
}) => {
  const columnConfig: any = realTimeOnTrends ? [] : [getActionColumn(dimension)];

  // first column is the dimension value
  columnConfig.push({
    title: '',
    dataKey: 'name',
    key: dimension,
    width: DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    frozen: 'left',
    resizable: true,
    cellRenderer: ({ cellData, column, rowData }) => (
      <Cell cellData={cellData} column={column} rowData={rowData} />
    ),
    headerRenderer: (props) => <Header {...props} />,
  });

  // remaining columns are the metric values
  metrics.forEach((metric, idx) => {
    const metricInfo = getMetricMetaByKey({ metricKey: metric });
    const showNA = showNAForMetric ? showNAForMetric(metric) : false;

    const evenCol = (idx + 1) % 2 === 0;

    columnConfig.push({
      title: metricInfo.short,
      dataKey: metric,
      key: metric,
      metric,
      metricInfo,
      flexGrow: 1,
      align: 'right',
      width: METRIC_COL_WIDTH,
      minWidth: METRIC_COL_WIDTH,
      sortable: true,
      cellRenderer: ({ cellData, column, container, rowData }) => {
        return (
          <ValueCell
            cellData={showNA ? 'NA' : cellData}
            column={column}
            container={container}
            rowData={rowData}
          />
        );
      },
      headerRenderer: (props) => (
        <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_HELPTEXT} showTrend={showTrend} />
      ),
      className: evenCol ? 'table-zebra-even' : 'table-zebra-odd',
    });

    const mfMetricAbs = getMetricFilterMetric(metric);
    const mfMetricPct = getMetricFilterPctMetric(metric);
    const mfTitle = `[${MF_LABEL}]`; // + metricInfo.short;

    if (!showTrend) {
      columnConfig.push({
        title: mfTitle,
        dataKey: mfMetricAbs,
        key: mfMetricAbs,
        metric,
        metricInfo,
        flexGrow: 1,
        align: 'right',
        width: SF_COL_WIDTH,
        minWidth: SF_COL_WIDTH,
        sortable: true,
        mfPctChecked: false, // indicator to always use unit as % when %MF is selected
        sfMetric: true,
        cellRenderer: ({ cellData, column, container, rowData }) => (
          <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
        ),
        headerRenderer: (props) => (
          <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_HELPTEXT} />
        ),
        className: evenCol ? 'table-zebra-even' : 'table-zebra-odd',
        headerClassName: 'sf-highlight',
      });

      columnConfig.push({
        title: mfTitle + ' %',
        dataKey: mfMetricPct,
        key: mfMetricPct,
        metric,
        metricInfo,
        flexGrow: 1,
        align: 'right',
        width: SF_COL_WIDTH,
        minWidth: SF_COL_WIDTH,
        sortable: true,
        mfPctChecked: true, // indicator to always use unit as % when %MF is selected
        sfMetric: true,
        cellRenderer: ({ cellData, column, container, rowData }) => (
          <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
        ),
        headerRenderer: (props) => (
          <MetricHeader {...props} helpLookup={COLUMN_HEADER_METRIC_FILTER_PCT_HELPTEXT} />
        ),
        className: evenCol ? 'table-zebra-even' : 'table-zebra-odd',
        headerClassName: 'sf-highlight',
      });
    }
  });

  return columnConfig;
};

export function formatMultiData({
  data = {},
  sortBy,
  searchText = '',
  metrics,
  hideUnknownRows = false,
  getUnknownName,
}: {
  data: Record<string, any>;
  sortBy: {
    key: string;
    order: 'desc' | 'asc';
  };
  searchText?: string;
  metrics: string[];
  hideUnknownRows?: boolean;
  getUnknownName?: (data: Record<string, any>) => any;
}) {
  const { metadataFilterData = {}, metricFilterData = {} }: any = data;

  // move unknown rows to bottom
  let orderedRows: any = [];
  const nanRows: unknown[] = [];
  const unknownRows: any = [];
  let totals = {};

  if (
    get(metadataFilterData, ['rows'], []).length > 0 &&
    get(metadataFilterData, ['rows'], []).length > 0
  ) {
    for (let i = 0; i < metadataFilterData.rows.length; i++) {
      const metaRow = metadataFilterData.rows[i];
      const metricRow = addMetricFilterTag(get(metricFilterData, ['rows', i], {}));

      // flattened
      const row = {
        ...metaRow,
        ...metricRow,
      };

      addRelativePctMetric(metrics, row);

      const name = getUnknownName ? getUnknownName(row) : row.name.join('');
      if (UNKNOWN_NAME.includes(name)) {
        unknownRows.push({ ...row, disableDrilldown: false });

        // Filter out all NaN, such as "NA"
        // because the presence of NA will cause _.orderBy not to work properly
      } else if (sortBy?.key && !isNumber(get(row, sortBy.key))) {
        nanRows.push(row);
      } else {
        orderedRows.push(row);
      }
    }

    if (sortBy) {
      orderedRows = orderBy(orderedRows, [sortBy.key], [sortBy.order]);
    }

    totals = {
      name: ['Total'],
      ...metadataFilterData.totals,
      ...addMetricFilterTag(metricFilterData.totals),
      disableDrilldown: true,
      hidePctShare: true,
      totalRow: true,
    };

    addRelativePctMetric(metrics, totals);
  }

  let filteredRows = orderedRows.concat(nanRows, hideUnknownRows ? [] : unknownRows);

  const pctShareMaxValues = calculatePctShareMax({
    data: filteredRows,
  });

  // search
  if (searchText) {
    const regex = new RegExp(searchText, 'i'); // ignore case
    filteredRows = filteredRows.filter((row) => regex.test(row.name.join('')));
  }

  return { rows: filteredRows, totals, pctShareMaxValues };
}

export const formatErrorColumns = ({
  dimension,
  metric,
  errorCodeInfo,
  realTimeOnTrends,
  showTrend = false,
}) => {
  const columnConfig: any = realTimeOnTrends ? [] : [getActionColumn(dimension)];
  const metricInfo = getMetricMetaByKey({ metricKey: metric });
  const errorMetricInfo = getMetricMetaByKey({ metricKey: errorCodeInfo.primary });
  // first column is the dimension value
  columnConfig.push({
    title: '',
    dataKey: 'name',
    key: dimension,
    metricInfo,
    errorCodeInfo,
    errorMetricInfo,
    width: DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    metric: errorCodeInfo.primary, // used for drilldown
    frozen: 'left',
    resizable: true,
    cellRenderer: ({ cellData, column, rowData }) => (
      <ErrorCell cellData={cellData} column={column} rowData={rowData} />
    ),
  });

  columnConfig.push({
    title: errorCodeInfo.columnName,
    dataKey: metric,
    key: metric,
    metric: metric,
    metricInfo,
    errorCodeInfo,
    errorMetricInfo,
    flexGrow: 1,
    align: 'right',
    width: METRIC_COL_WIDTH,
    minWidth: METRIC_COL_WIDTH,
    sortable: true,
    cellRenderer: ({ cellData, column, container, rowData }) => (
      <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
    ),
  });

  return columnConfig;
};

export const formatMultiErrorColumns = ({
  dimension,
  metric,
  errorCodeInfo,
  mfPctChecked = false,
  realTimeOnTrends,
  showTrend = false,
}) => {
  const columnConfig: any = realTimeOnTrends ? [] : [getActionColumn(dimension)];

  const metricInfo = getMetricMetaByKey({ metricKey: metric });
  const errorMetricInfo = getMetricMetaByKey({ metricKey: errorCodeInfo.primary });

  // first column is the dimension value
  columnConfig.push({
    title: '',
    dataKey: 'name',
    key: dimension,
    width: DIMENSION_COL_WIDTH,
    minWidth: DIMENSION_COL_WIDTH,
    metric: errorCodeInfo.primary, // used for drilldown
    metricInfo,
    frozen: 'left',
    resizable: true,
    cellRenderer: ({ cellData, column, rowData }) => (
      <ErrorCell cellData={cellData} column={column} rowData={rowData} />
    ),
  });

  columnConfig.push({
    title: errorCodeInfo.columnName,
    dataKey: metric,
    key: metric,
    metric: metric,
    flexGrow: 1,
    align: 'right',
    metricInfo,
    errorMetricInfo,
    width: METRIC_COL_WIDTH,
    minWidth: METRIC_COL_WIDTH,
    sortable: true,
    cellRenderer: ({ cellData, column, container, rowData }) => (
      <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
    ),
    className: 'table-zebra-odd',
  });

  const mfMetricAbs = getMetricFilterMetric(metric);
  const mfMetricPct = getMetricFilterPctMetric(metric);
  const mfTitle = `[${MF_LABEL}]`; // + metricInfo.short;

  if (!showTrend) {
    columnConfig.push({
      title: mfTitle, // + errorCodeInfo.columnName,
      dataKey: mfMetricAbs,
      key: mfMetricAbs,
      metricInfo,
      metric: metric,
      flexGrow: 1,
      align: 'right',
      width: METRIC_COL_WIDTH,
      minWidth: METRIC_COL_WIDTH,
      sortable: true,
      sfMetric: true,
      mfPctChecked: false, // indicator to always use unit as % when %MF is selected
      cellRenderer: ({ cellData, column, container, rowData }) => (
        <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
      ),
      headerClassName: 'sf-highlight',
      className: 'table-zebra-odd',
    });

    columnConfig.push({
      title: `${mfTitle} %`, // + errorCodeInfo.columnName,
      dataKey: mfMetricPct,
      key: mfMetricPct,
      metricInfo,
      metric: metric,
      flexGrow: 1,
      align: 'right',
      width: METRIC_COL_WIDTH,
      minWidth: METRIC_COL_WIDTH,
      sortable: true,
      sfMetric: true,
      mfPctChecked: true, // indicator to always use unit as % when %MF is selected
      cellRenderer: ({ cellData, column, container, rowData }) => (
        <ValueCell cellData={cellData} column={column} container={container} rowData={rowData} />
      ),
      headerClassName: 'sf-highlight',
      className: 'table-zebra-odd',
    });
  }

  return columnConfig;
};

// Supported dimensions of Map view
const MAP_SUPPORTED_DIMENSIONS = [
  'GEO_COUNTRY',
  'GEO_STATE',
  'GEO_CITY',
  'geo_country',
  'geo_state',
  'geo_city',
];

export const shouldMapEnabled = (dimension: string) => MAP_SUPPORTED_DIMENSIONS.includes(dimension);
